import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  getComplimentaryPeriodActionLabel,
  isComplimentaryPeriodUpdate,
} from 'src/components/SidePanelTabs/MembershipInformation/subscription-details.utils';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import {
  AddComplimentaryPeriodForm,
  ComplimentaryPeriodSectionValues,
} from './add-complimentary-period-form';
import {
  AddComplimentaryPeriodFormField,
  COMPLIMENTARY_PERIOD_SECTION,
  getCppLengthList,
  TODAY_DATE,
} from './const';
import { prepareCppData } from './util';

export interface CppPayload {
  expDate: string;
  partnerProductId: string;
}

export const AddComplimentaryPeriodModal = () => {
  const { closeModal } = useModalMethods(Modals.AddComplimentaryPeriod);
  const { isOpened, modalProps } = useModalState(Modals.AddComplimentaryPeriod);

  const formMethods = useForm<ComplimentaryPeriodSectionValues>({
    mode: 'onChange',
    defaultValues: {
      [COMPLIMENTARY_PERIOD_SECTION]: {
        [AddComplimentaryPeriodFormField.PartnerProductId]: '',
        [AddComplimentaryPeriodFormField.CppExpirationDate]:
          getCppLengthList(TODAY_DATE)[0].value,
      },
    },
  });
  const { formState, handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: ComplimentaryPeriodSectionValues) => {
      if (!modalProps?.address?.addressId || !modalProps?.address?.partnerId) {
        return;
      }

      const cppData = prepareCppData(data[COMPLIMENTARY_PERIOD_SECTION]);

      formMethods.reset();
      closeModal();

      await modalProps.addCppToSchedule({
        addressId: modalProps?.address?.addressId,
        partnerId: modalProps?.address?.partnerId,
        cpp: cppData,
      });
    },
    [closeModal, modalProps, formMethods],
  );

  const handleClose = useCallback(() => {
    formMethods.reset();
    closeModal();
  }, [closeModal, formMethods]);

  const cppExpDate = modalProps?.address?.cppExpDate;

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title={getComplimentaryPeriodActionLabel(cppExpDate)}
      actions={[
        {
          label: 'Cancel',
          kind: 'secondary',
          testId: 'add-complimentary-period-cancel',
        },
        {
          label: isComplimentaryPeriodUpdate(cppExpDate)
            ? 'Update membership'
            : 'Add membership',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          testId: 'complimentary-period-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="add-complimentary-period-modal"
    >
      <GenericForm<ComplimentaryPeriodSectionValues> formMethods={formMethods}>
        <AddComplimentaryPeriodForm address={modalProps?.address} />
      </GenericForm>
    </ModalContainer>
  );
};
